import React from "react";
import { Delete, Check, X } from "lucide-react";

/**
 * Big numpad overlay for selecting a song by 3-digit code (A001 → A999).
 * Designed for shaky hands on stage: huge tap targets (≥80px), high contrast.
 */
export const NumpadDialog = ({ open, onClose, songs, onSelect }) => {
  const [digits, setDigits] = React.useState([]);

  React.useEffect(() => {
    if (open) setDigits([]);
  }, [open]);

  // Keyboard support (numeric keys, backspace, escape, enter)
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) {
        setDigits((d) => (d.length < 3 ? [...d, e.key] : d));
      } else if (e.key === "Backspace") {
        setDigits((d) => d.slice(0, -1));
      } else if (e.key === "Escape") {
        onClose?.();
      } else if (e.key === "Enter") {
        commit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [open, digits]);

  const tap = (n) => {
    setDigits((d) => (d.length < 3 ? [...d, String(n)] : d));
  };

  const backspace = () => setDigits((d) => d.slice(0, -1));

  const commit = () => {
    if (digits.length === 0) return;
    const id = `A${digits.join("").padStart(3, "0")}`;
    const match = songs.find((s) => s.id === id);
    if (!match) {
      // brief flash
      setDigits([]);
      return;
    }
    onSelect?.(id);
    onClose?.();
  };

  // Auto-commit when 3 digits typed
  React.useEffect(() => {
    if (open && digits.length === 3) {
      const t = setTimeout(commit, 150);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [digits, open]);

  if (!open) return null;

  const id = `A${digits.join("").padEnd(3, "_")}`;
  const matchedSong = digits.length > 0 ? songs.find((s) => s.id === `A${digits.join("").padStart(3, "0")}`) : null;

  const KeyBtn = ({ children, onClick, accent, testId, big = false }) => (
    <button
      onClick={onClick}
      className={`flex items-center justify-center select-none active:scale-[0.97] transition rounded-2xl font-display tabular-nums ${
        big ? "text-5xl sm:text-6xl" : "text-5xl sm:text-6xl"
      } ${
        accent === "gold"
          ? "bg-[var(--gl-gold)] text-black hover:brightness-110"
          : accent === "neon"
          ? "bg-[var(--gl-neon)] text-black hover:brightness-110"
          : accent === "danger"
          ? "bg-[#1a1a1a] border border-orange-500/40 text-orange-400 hover:border-orange-500"
          : "bg-[#161616] border border-white/10 text-white hover:border-[var(--gl-gold)]/50 hover:bg-[#1c1c1c]"
      }`}
      style={{ minHeight: 80 }}
      data-testid={testId}
    >
      {children}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/97 backdrop-blur-sm flex flex-col"
      data-testid="numpad-dialog"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 safe-bottom">
        <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">
          Pavé numérique
        </div>
        <button
          onClick={onClose}
          className="w-11 h-11 rounded-full bg-[#1a1a1a] border border-white/10 flex items-center justify-center hover:bg-white/5"
          data-testid="numpad-close"
          aria-label="Fermer"
        >
          <X size={20} />
        </button>
      </div>

      <div className="px-5 py-4 text-center">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
          Morceau
        </div>
        <div
          className="font-display text-7xl sm:text-8xl text-[var(--gl-gold)] tabular-nums"
          data-testid="numpad-display"
        >
          {id}
        </div>
        <div className="h-6 mt-2 text-sm">
          {matchedSong ? (
            <span className="text-[var(--gl-gold)] font-bold truncate inline-block max-w-full px-4">
              {matchedSong.title}
            </span>
          ) : digits.length === 3 ? (
            <span className="text-orange-400 font-bold uppercase tracking-wider text-xs">
              Aucun morceau {id}
            </span>
          ) : (
            <span className="text-neutral-600">
              Tape 3 chiffres
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 px-5 pb-5 grid grid-cols-3 gap-3 content-end">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <KeyBtn key={n} onClick={() => tap(n)} testId={`numpad-${n}`}>
            {n}
          </KeyBtn>
        ))}
        <KeyBtn onClick={backspace} accent="danger" testId="numpad-backspace">
          <Delete size={28} />
        </KeyBtn>
        <KeyBtn onClick={() => tap(0)} testId="numpad-0">0</KeyBtn>
        <KeyBtn onClick={commit} accent="gold" testId="numpad-commit">
          <Check size={32} />
        </KeyBtn>
      </div>
    </div>
  );
};

export default NumpadDialog;
